import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashXor from 'lodash/fp/xor';
import { withRouter } from 'react-router';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  isApplicationPastStatus,
  getHouseholdEarliestApplicationStatus,
} from 'common/householdApplication/applicationHelpers';

import { USER_ROLE_ADMIN, USER_ROLE_HOUSEHOLD_ADMIN } from 'common/userRoles';

import {
  HOUSEHOLD_LISTING_STATUS_ACTIVE,
  HOUSEHOLD_APPLICATION_STATUS_COLUMNS,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
} from 'common/householdConstants';

import { SCREEN_ID_HOUSEHOLD_DASHBOARD_KANBAN_VIEW } from 'client/constants/screenIds';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
  DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_TOURING,
} from 'common/dataTableConstants';

import SrLogo from 'client/components/SrLogo';
import SrSimpleDropdown from 'client/components/SrSimpleDropdown';
import SrUserSettingListingManagementCompanyDropdown, {
  SR_USER_SETTING_LISTING_MANAGEMENT_COMPANY_FILTER_BY_ALL,
} from 'client/components/SrUserSettingListingManagementCompanyDropdown';
import SrSearchByPhoneHouseholdIdAndRedirectForm from 'client/components/SrSearchByPhoneAndRedirectForm';
import ButtonDeprecated, { BUTTON_THEME_BLUE } from 'client/components/ButtonDeprecated';
import SrHouseholdKanbanBoardSwimlane, {
  getDataTableHouseholdKanbanBoardTourSwimlanesSettingsFromProps,
} from 'client/components/SrHouseholdKanbanBoardSwimlane';

import SrHouseholdKanbanBoardSwimlaneRentlinxLeads, {
  getDataTableSettingsFromProps as getDataTableRentlinxLeadsSettingsFromProps,
} from 'client/components/SrHouseholdKanbanBoardSwimlaneRentlinxLeads';
import SrHouseholdKanbanBoardApplicationColumn from 'client/components/SrHouseholdKanbanBoardApplicationColumn';
import SrUserSettingListingServicesDropdown from 'client/components/SrUserSettingListingServicesDropdown';
import SrHouseholdKanbanBoard from 'client/components/SrHouseholdKanbanBoard';

import UserActions from 'client/actions/User';
import PartnerActions from 'client/actions/Partner';
import LeasingManagerActions from 'client/actions/LeasingManager';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';
import { actionDataTableLoadItems, actionDataTableReset } from 'client/helpers/dataTables';
import SrUserSettingLeasingManagerDropdown, {
  SR_USER_SETTING_LEASING_MANAGER_FILTER_BY_ALL,
} from 'client/components/SrUserSettingLeasingManagerDropdown';

function getListingIdsWithAppsInReadyForLease(households) {
  const listingIds = [];

  for (const household of households) {
    for (const listingApplication of household.listingApplications) {
      const householdApplicationStatus = getHouseholdEarliestApplicationStatus(
        listingApplication.applications,
      );
      const hasHouseholdPassPendingLandlordStatus = isApplicationPastStatus(
        {
          status: householdApplicationStatus,
        },
        HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
      );

      if (
        hasHouseholdPassPendingLandlordStatus &&
        listingApplication.status === HOUSEHOLD_LISTING_STATUS_ACTIVE
      ) {
        listingIds.push(listingApplication.listing.id);
      }
    }
  }

  return listingIds;
}

@withRouter
@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_HOUSEHOLD_ADMIN],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_HOUSEHOLD_DASHBOARD_KANBAN_VIEW)
@withScrollToTop
@branch({
  currentUser: ['currentUser'],
  partnersList: ['partners', 'list'],
  leasingManagers: ['leasingManagers', 'listWithPartners'],
  householdApplicationItems: [
    'dataTables',
    'DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN',
    'items',
  ],
})
class HouseholdKanbanBoardPage extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    partnersList: PropTypes.array,
    leasingManagers: PropTypes.array,
  };

  static fetchData(match, inject) {
    return Promise.all([
      PartnerActions.getPartnerList(inject),
      LeasingManagerActions.getListWithPartners(inject),
    ]);
  }

  constructor(props) {
    super(props);

    const { currentUser } = props;
    let moveInDateRangesSelected = [
      `${momentWithAustinTimezone().year()}_${momentWithAustinTimezone().month()}`,
    ];

    const appProSettings = currentUser.settings.appProSettings || {};

    if (appProSettings.moveInDateRanges) {
      moveInDateRangesSelected = appProSettings.moveInDateRanges;
    }

    this.state = {
      moveInDateRangesSelected,
      tempMoveInDataRangesSelected: moveInDateRangesSelected,
    };
  }

  _onSelectCreatedDateRangesSelectedOption = (createdDateRangeSelected) => {
    const { tempMoveInDataRangesSelected } = this.state;
    const newState = {
      tempMoveInDataRangesSelected: tempMoveInDataRangesSelected.includes(createdDateRangeSelected)
        ? tempMoveInDataRangesSelected.filter((id) => id !== createdDateRangeSelected)
        : [...tempMoveInDataRangesSelected, createdDateRangeSelected],
    };

    this.setState(newState);
  };

  _onUpdateCreatedDateRangesOption = () => {
    const newState = {
      moveInDateRangesSelected: this.state.tempMoveInDataRangesSelected,
    };

    this.setState(newState, this._reloadDataTableHouseholdApplications);

    this._updateUserSettings({
      moveInDateRanges: newState.moveInDateRangesSelected,
    });
  };

  _onUpdateLeasingManagerUserId = (leasingManagerUserId) => {
    this._reloadDataTableHouseholdApplications().then(() => {
      this._updateUserSettings({
        leasingManagerUserId,
        listingManagementCompany: undefined,
      });
    });
  };

  _updateUserSettings = (newAppProSettings) => {
    const { dispatch, currentUser } = this.props;
    const appProSettings = currentUser.settings.appProSettings || {};

    return dispatch(UserActions.updateSettings, {
      appProSettings: {
        ...appProSettings,
        ...newAppProSettings,
      },
    });
  };

  _getDataTableHouseholdApplicationsProps = () => {
    const { leasingManagers, partnersList, currentUser } = this.props;
    const { moveInDateRangesSelected } = this.state;

    const userSettings = currentUser.settings.appProSettings || {};

    let leasingManagerUserId = userSettings.leasingManagerUserId;
    if (leasingManagerUserId === SR_USER_SETTING_LEASING_MANAGER_FILTER_BY_ALL) {
      leasingManagerUserId = leasingManagers.map(({ id }) => id);
    }

    let listingManagementCompany = userSettings.listingManagementCompany;
    if (listingManagementCompany === SR_USER_SETTING_LISTING_MANAGEMENT_COMPANY_FILTER_BY_ALL) {
      listingManagementCompany = partnersList
        .filter(({ invalidSignup }) => !invalidSignup)
        .map(({ managementCompanyName }) => managementCompanyName);
    }

    const listingServices = userSettings.listingServices;

    const moveInDateRanges = moveInDateRangesSelected.map((moveInDateRangeSelected) => {
      const [year, month] = moveInDateRangeSelected.split('_');
      const date = momentWithAustinTimezone()
        .date(1)
        .month(parseInt(month, 10))
        .year(parseInt(year, 10));

      return [date.startOf('month').toISOString(), date.endOf('month').toISOString()];
    });

    return {
      moveInDateRanges,
      listingManagementCompany,
      listingServices,
      leasingManagerUserId,
    };
  };

  _reloadDataTableHouseholdApplications = () => {
    const { dispatch } = this.props;
    const dataTableProps = this._getDataTableHouseholdApplicationsProps();

    return Promise.all([
      dispatch(actionDataTableReset, getDataTableRentlinxLeadsSettingsFromProps(dataTableProps)),
      dispatch(
        actionDataTableReset,
        getDataTableHouseholdKanbanBoardTourSwimlanesSettingsFromProps({
          dataTableSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_TOURING,
          ...dataTableProps,
        }),
      ),
    ]);
  };

  _getRedirectionPath = (user) => {
    const { dispatch } = this.props;

    return dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD,
      viewId: `search-user-id-${user.id}`,
      customParams: {
        householdDashboard: {
          userId: user.id,
        },
      },
      count: 1,
    }).then(({ items }) => {
      return items.length ? `/admin/household/all/${user.id}` : `/admin/tours/all/user/${user.id}`;
    });
  };

  render() {
    const {
      currentUser,
      dispatch,
      leasingManagers,
      history,
      householdApplicationItems,
    } = this.props;
    const { moveInDateRangesSelected, tempMoveInDataRangesSelected } = this.state;

    const moveInDateRangeOptions = [];
    const moveInDateRangeOptionsSelected = [];

    for (let monthIndex = -6; monthIndex <= 6; monthIndex += 1) {
      const date = momentWithAustinTimezone().set('date', 1).add(monthIndex, 'months');
      const dateId = `${date.year()}_${date.month()}`;
      const moveInDateRangeOption = {
        id: dateId,
        title: date.format('MMMM YYYY').toLowerCase(),
        isActive: tempMoveInDataRangesSelected.includes(dateId),
      };

      moveInDateRangeOptions.push(moveInDateRangeOption);

      if (moveInDateRangesSelected.includes(dateId)) {
        moveInDateRangeOptionsSelected.push(moveInDateRangeOption.title);
      }
    }

    const appProSettings = currentUser.settings.appProSettings || {};
    const kanbanBoardSettings = this._getDataTableHouseholdApplicationsProps();
    const listingIdsWithAppsInReadyForLease = getListingIdsWithAppsInReadyForLease(
      householdApplicationItems || [],
    );

    return (
      <div className="kanbanBoardPageContainer">
        <div className="page-householdKanbanBoard">
          <div className="admin-navbar">
            <div className="logo-container">
              <SrLogo redirectTo="/admin" />
            </div>
            <div className="admin-navbar-links-container">
              <div className="admin-navbar-link">
                <SrSimpleDropdown
                  title={`month: ${moveInDateRangeOptionsSelected.join(',')}`}
                  options={moveInDateRangeOptions}
                  onChange={this._onSelectCreatedDateRangesSelectedOption}
                  multiple
                  footer={
                    <div className="buttonContainer">
                      <ButtonDeprecated
                        theme={BUTTON_THEME_BLUE}
                        type="button"
                        onClick={this._onUpdateCreatedDateRangesOption}
                        disabled={
                          lodashXor(moveInDateRangesSelected, tempMoveInDataRangesSelected)
                            .length === 0
                        }
                      >
                        save
                      </ButtonDeprecated>
                    </div>
                  }
                />
              </div>
              <div className="admin-navbar-link">
                <SrUserSettingLeasingManagerDropdown
                  leasingOperationManagerUserId={appProSettings.leasingManagerUserId}
                  leasingOperationManagers={leasingManagers || []}
                  onUpdate={this._onUpdateLeasingManagerUserId}
                />
              </div>
              <div className="admin-navbar-link">
                <SrUserSettingListingManagementCompanyDropdown
                  currentUser={currentUser}
                  settingField="appProSettings.listingManagementCompany"
                  resetAccountManagerUserId
                />
              </div>
              <div className="admin-navbar-link">
                <SrUserSettingListingServicesDropdown
                  currentUser={currentUser}
                  settingField="appProSettings.listingServices"
                  dispatch={dispatch}
                />
              </div>
            </div>
            <SrSearchByPhoneHouseholdIdAndRedirectForm
              getRedirectionPath={this._getRedirectionPath}
            />
          </div>
        </div>
        <div className="admin-content">
          <div className="SrHouseholdKanbanBoardSwimlane">
            <div className="columns">
              <SrHouseholdKanbanBoardSwimlaneRentlinxLeads
                {...kanbanBoardSettings}
                currentUser={currentUser}
                dispatch={dispatch}
                history={history}
              />
              <SrHouseholdKanbanBoardSwimlane
                dataTableSource={DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_TOURING}
                {...kanbanBoardSettings}
              />
              <SrHouseholdKanbanBoard
                moveInDateRanges={kanbanBoardSettings.moveInDateRanges}
                listingManagementCompany={kanbanBoardSettings.listingManagementCompany}
                listingServices={kanbanBoardSettings.listingServices}
                accountManagerUserId={kanbanBoardSettings.accountManagerUserId}
                leasingManagerUserId={kanbanBoardSettings.leasingManagerUserId}
                listingIdsWithAppsInReadyForLease={listingIdsWithAppsInReadyForLease}
              />
              {[
                HOUSEHOLD_APPLICATION_STATUS_COLUMNS[4],
                HOUSEHOLD_APPLICATION_STATUS_COLUMNS[5],
                HOUSEHOLD_APPLICATION_STATUS_COLUMNS[6],
              ].map((column) => (
                <SrHouseholdKanbanBoardApplicationColumn
                  key={column.applicationStatus}
                  {...kanbanBoardSettings}
                  {...column}
                  dispatch={dispatch}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

HouseholdKanbanBoardPage.displayName = 'HouseholdKanbanBoardPage';

export default HouseholdKanbanBoardPage;
